import React, { useEffect, useState } from 'react';
import { crmService } from '../../services/central-api/crm';
import Container from '../../components/Container';
import Header from '../../components/Header';
import Kanban from '../../components/Kanban';
import { translate } from '../../services/translate';
import { useQuery } from '@tanstack/react-query';
import { NavigationProp, RouteProp, useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import { removeDeletedDateFromSerialNo } from '../../utils/removeDeletedDateFromSerialNo';
import CrmCompaniesTicketStatusDropdown from '../../components/crm/CrmCompaniesTicketStatusDropdown';
import { View } from 'react-native';
import { CRMParamList } from '../../typings/Params';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../components/MyAppText';
import { Moment } from 'moment';
import moment from 'moment';


export default function OfflineEventsKanban() {
    const { styles } = useStyles(styleSheet);
    const navigation = useNavigation<NavigationProp<CRMParamList>>();

    const [selectedCompany, setSelectedCompany] = useState<CompanyTicketStatus>();
    const route = useRoute<RouteProp<CRMParamList, 'OfflineEventsKanban'>>();
    const [companyId, setCompanyId] = useState<number>(route.params?.companyId ?? 0);

    useEffect(() => {
        if (companyId) {
            navigation.setParams({
                companyId,
            });
        }
    }, [companyId]);

    const isFocused = useIsFocused();
    const { data } = useQuery({
        queryKey: ['getKanbanTickets', selectedCompany],
        queryFn: () => crmService.getKanbanTickets({ companyId: selectedCompany!.id }),
        enabled: isFocused && Boolean(selectedCompany),
        refetchInterval: isFocused ? 30000 : false,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
    });

    /**
        *
        * @param intervals
        * @returns time in minutes
        */
    function calculateSlaTotal(intervals: { start: Moment; finish: Moment; }[]) {

        const sorted = intervals
            .slice()
            .sort((a, b) => a.start.valueOf() - b.start.valueOf());

        const merged = [];

        for (const current of sorted) {
            const last = merged[merged.length - 1];

            if (!last || current.start.isAfter(last.finish)) {
                merged.push({
                    start: current.start.clone(),
                    finish: current.finish.clone(),
                });
            } else {
                last.finish = moment.max(last.finish, current.finish);
            }
        }

        return merged.reduce((total, i) => total + i.finish.diff(i.start, 'minutes'), 0);
    }

    function calculateSla(ticket: KanbanTicket) {

        if (!ticket.finish) {
            const time = calculateSlaTotal([
                // default time for ticket to be validated
                // FIXME: remove hardcoded 50 min
                { start: moment(ticket.start), finish: moment(ticket.start).add(50, 'minutes') },
                ...ticket.reports.map(report => ({
                    start: moment(report.createdAt),
                    finish: moment(report.createdAt).add(moment.duration(report.reason.duration, 'minutes')),
                }))]);

            const ticketDuration = moment().diff(moment(ticket.start), 'minutes');

            const percent = ticketDuration / time;

            if (percent < .25) {
                return 'onTime';
            } else if (percent < .5) {
                return 'onGoing';
            } else if (percent < .75) {
                return 'warning';
            } else {
                return 'critical';
            }

        } else {

            let time = 0;

            if (ticket.reports.length) {
                time = calculateSlaTotal([
                    // FIXME: default time for ticket to be validated
                    { start: moment(ticket.start), finish: moment(ticket.start).add(50, 'minutes') },
                    ...ticket.reports.map(report => ({
                        start: moment.min(moment(ticket.finish), moment(report.createdAt)),
                        finish: moment.min(moment(ticket.finish), moment(report.createdAt).add(moment.duration(report.reason.duration, 'minutes'))),
                    }))]);
            }

            const ticketDuration = moment(ticket.finish).diff(moment(ticket.start), 'minutes');



            if (time >= ticketDuration) {
                return 'onTime';
            } else {
                return 'critical';
            }
        }

    }

    return <Container>
        <Header
            title={translate('offlineEventsKanbanTitle')}
            actions={<CrmCompaniesTicketStatusDropdown
                value={companyId}
                setValue={setCompanyId}
                zIndex={2}
                viewStyle={{ minWidth: 225 }}
                onSelect={(company: CompanyTicketStatus) => {
                    setSelectedCompany(company);
                }}
            />}
        />
        <View style={{ zIndex: -99, flex: 1 }}>
            {selectedCompany &&
                <Kanban
                    columns={[{
                        key: 'open',
                        label: translate('open'),
                    }, ...selectedCompany.ticketStatuses.map(status => ({
                        key: String(status.id),
                        label: status.title,
                    })), {
                        key: 'concluded',
                        label: translate('concluded')
                    }]}
                    cards={(data ?? []).map(ticket => {
                        return {
                            column: ticket.finish ? 'concluded' : !ticket.statusHistory?.length ? 'open' : String(ticket.statusHistory[0].statusId),
                            content: ticket,
                            key: String(ticket.id),
                        };
                    })}
                    onClickCard={({ content: item }) => {
                        navigation.navigate('KanbanTicketDetail', { id: item.id });
                    }}
                    cardContent={(item) => ({
                        id: item.content.id,
                        start: item.content.start,
                        title: translate('cameraDisconnection'),
                        finish: item.content.finish,
                        responsible: item.content.responsibleUser,
                        duration: {
                            start: item.content.start,
                            finish: item.content.finish,
                            status: calculateSla(item.content),
                        },
                        additionalInfo: [
                            item.content.camera.serialNo ? removeDeletedDateFromSerialNo(item.content.camera.serialNo) : item.content.camera.title,
                        ],
                        tags: item.content.reports?.reduce((prev, report) => {
                            if (!prev.find(r => r.id === report.reason.id)) {
                                prev.push(report.reason);
                            }
                            return prev;
                        }, [] as { id: number; name: string; color: string; }[])
                    })}
                />
            }
            {!selectedCompany && <View style={styles.allSpaceCentered}>
                <MyAppText style={styles.mainHeaderText}>{translate('requiredCompanySelection')}</MyAppText>
            </View>}
        </View>
    </Container>;

}


const styleSheet = createStyleSheet((theme) => ({
    mainHeaderText: {
        fontSize: 22,
        color: theme.colors.neutral['500'],
        fontWeight: '700',
    },
    allSpaceCentered: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
}));

